<!-- Angular 16 compatible template (*ngIf / *ngFor) -->
<button
  *ngIf="!inline()"
  type="button"
  class="rag-launcher"
  [class.rag-launcher--open]="isOpen()"
  [attr.aria-expanded]="isOpen()"
  [attr.aria-label]="isOpen() ? text().launcherClose : text().launcherOpen"
  (click)="toggle()"
>
  <span class="rag-launcher__icon">
    <rag-icon [name]="isOpen() ? 'close' : 'assistant'" [size]="isOpen() ? 24 : 56"></rag-icon>
  </span>
  <span *ngIf="unread() && !isOpen()" class="rag-launcher__badge" aria-hidden="true">{{ unread() }}</span>
</button>

<section
  *ngIf="panelVisible()"
  class="rag-panel"
  role="dialog"
  aria-modal="false"
  [attr.aria-label]="text().panelTitle"
>
  <header class="rag-panel__head">
    <span class="rag-panel__glyph" aria-hidden="true">
      <rag-icon name="assistant" [size]="32"></rag-icon>
    </span>
    <div class="rag-panel__identity">
      <p class="rag-panel__title">{{ text().panelTitle }}</p>
      <p class="rag-panel__subtitle">{{ text().panelSubtitle }}</p>
    </div>
    <div class="rag-panel__actions">
      <button
        type="button"
        class="rag-ghost-btn"
        [attr.aria-label]="text().reset"
        [title]="text().reset"
        [disabled]="!hasConversation()"
        (click)="resetThread()"
      >
        <rag-icon name="refresh" [size]="17"></rag-icon>
      </button>
      <button
        *ngIf="!inline()"
        type="button"
        class="rag-ghost-btn"
        [attr.aria-label]="text().minimize"
        [title]="text().minimize"
        (click)="close()"
      >
        <rag-icon name="minimize" [size]="17"></rag-icon>
      </button>
    </div>
  </header>

  <div class="rag-thread" #thread (scroll)="onThreadScroll($event)">
    <div *ngIf="!messages().length" class="rag-welcome">
      <span class="rag-welcome__glyph" aria-hidden="true">
        <rag-icon name="assistant" [size]="58"></rag-icon>
      </span>
      <h2 class="rag-welcome__heading">{{ text().welcomeHeading }}</h2>
      <p class="rag-welcome__hint">{{ text().welcomeHint }}</p>
    </div>

    <div *ngIf="useVirtual()" class="rag-thread__spacer" [style.height.px]="virtualWindow().padTop" aria-hidden="true"></div>

    <div
      *ngFor="let msg of virtualWindow().items; trackBy: trackById"
      class="rag-thread__row"
      [attr.data-rag-msg-id]="msg.id"
    >
      <rag-ask-message
        [message]="msg"
        [labels]="text()"
        [showSources]="cfg().showSources"
        [showScores]="cfg().showScores"
        [showFreshness]="cfg().showFreshness"
        [locale]="cfg().locale"
        [downloadMarker]="cfg().downloadLinkMarker"
        (download)="onDownload($event)"
      ></rag-ask-message>
    </div>

    <div *ngIf="useVirtual()" class="rag-thread__spacer" [style.height.px]="virtualWindow().padBottom" aria-hidden="true"></div>
    <div class="rag-thread__anchor" aria-hidden="true"></div>
  </div>

  <footer class="rag-composer">
    <div *ngIf="collectionList().length" class="rag-scope" [class.rag-scope--active]="scopeValue() !== ''">
      <rag-icon name="collection" [size]="14"></rag-icon>
      <select class="rag-scope__select" [attr.aria-label]="text().collectionPick" (change)="onCollectionChange($event)">
        <option value="" [selected]="scopeValue() === ''">{{ text().allCollections }}</option>
        <option *ngFor="let item of collectionList(); trackBy: trackByCollection" [value]="item.id" [selected]="isScoped(item.id)">
          {{ item.name }}
        </option>
      </select>
      <rag-icon name="chevron-down" [size]="13"></rag-icon>
      <button
        *ngIf="scopeValue() !== ''"
        type="button"
        class="rag-scope__clear"
        [attr.aria-label]="text().collectionClear"
        [title]="text().collectionClear"
        (click)="clearScope()"
      >
        <rag-icon name="close" [size]="12"></rag-icon>
      </button>
    </div>

    <div class="rag-input" [class.rag-input--focused]="focused()">
      <textarea
        #input
        class="rag-input__field"
        rows="1"
        [attr.maxlength]="cfg().maxChars"
        [attr.placeholder]="text().placeholder"
        [attr.aria-label]="text().placeholder"
        [disabled]="isStreaming()"
        (input)="onInput($event)"
        (keydown)="onKeydown($event)"
        (focus)="focused.set(true)"
        (blur)="focused.set(false)"
      ></textarea>

      <div class="rag-input__actions">
        <span *ngIf="showCounter()" class="rag-input__counter" aria-live="polite">
          {{ remaining() }} {{ text().charsRemaining }}
        </span>
        <button
          type="button"
          class="rag-send"
          [class.rag-send--active]="canSend() && !isStreaming()"
          [class.rag-send--stop]="isStreaming()"
          [disabled]="!isStreaming() && !canSend()"
          [attr.aria-label]="isStreaming() ? text().stop : text().send"
          [title]="isStreaming() ? text().stop : text().send"
          (click)="submit()"
        >
          <rag-icon [name]="isStreaming() ? 'stop' : 'send'" [size]="18"></rag-icon>
        </button>
      </div>
    </div>

    <p class="rag-disclaimer">{{ text().disclaimer }}</p>
  </footer>
</section>

<div
  *ngIf="preview.entries() as entries"
  #popover
  class="rag-popover"
  [class.rag-popover--placed]="preview.placed()"
  role="tooltip"
  [style.top.px]="preview.position()?.top"
  [style.left.px]="preview.position()?.left"
  (mouseenter)="preview.cancelHide()"
  (mouseleave)="preview.scheduleHide()"
>
  <div *ngFor="let entry of entries; trackBy: trackByEntry" class="rag-popover__entry">
    <span class="rag-popover__label">{{ text().sourcePreview }} {{ entry.rank }}</span>
    <p *ngIf="entry.source.title || entry.source.source" class="rag-popover__title">
      {{ entry.source.title || entry.source.source }}
    </p>
    <div *ngIf="entry.source.extract_md" class="rag-md rag-md--compact" [innerHTML]="entry.source.extract_md | ragMarkdown"></div>
    <span *ngIf="!entry.source.extract_md" class="rag-popover__empty">{{ text().extractUnavailable }}</span>
  </div>
</div>
